import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { PieChart } from "react-minimal-pie-chart";
import { Link } from "react-router-dom";

import type {
  Site,
  SitePerformanceReport,
  SiteVisibilityReport,
} from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Icon } from "~/assets/icons/Icon";
import Card from "~/components/ui/card/Card";
import { tryCatch } from "~/helpers/tryCatch";
import { routes } from "~/router";

import styles from "./VisibilityPerformanceCard.module.scss";
import { bandFromValue, perfBandFromMetrics } from "./bands";
import { isEngineHealthy } from "./engineHealth";

interface Props {
  site: Site | null | undefined;
}

const QUERY_STALE_TIME_MS = 5 * 60 * 1000;

function pickDesktop<T extends { platform: "desktop" | "mobile" }>(
  rows: T[] | undefined,
): T | null {
  return rows?.find((r) => r.platform === "desktop") ?? null;
}

function formatMs(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

function freshestUpdatedAt(
  vis: SiteVisibilityReport | null,
  perf: SitePerformanceReport | null,
): string | null {
  const candidates = [vis?.updated_at, perf?.updated_at].filter(
    (s): s is string => !!s,
  );
  if (candidates.length === 0) return null;

  return candidates.sort().reverse()[0];
}

const BAND_CLASS = {
  good: styles.bandGood,
  warn: styles.bandWarn,
  bad: styles.bandBad,
} as const;

// Pie chart needs concrete color values (SVG fill), so we hard-code the
// light-mode hex from --service-{green,yellow,red}3. Matches the visibility
// engine dashboard's palette. Dark-mode parity would need a getComputedStyle
// read like VisibilityEnginePage's readChartVars — skipped for now.
const SEGMENT_COLOR = {
  fully_accessible: "#0e9858", // --service-green3
  partially_accessible: "#a98900", // --service-yellow3
  js_dependent: "#cd4444", // --service-red3
} as const;

const SEGMENT_DOT_CLASS = {
  fully_accessible: styles.distSegmentGood,
  partially_accessible: styles.distSegmentWarn,
  js_dependent: styles.distSegmentBad,
} as const;

const SEGMENT_LABEL = {
  fully_accessible: "Fully",
  partially_accessible: "Partial",
  js_dependent: "JS-dep",
} as const;

const SEGMENTS = [
  "fully_accessible",
  "partially_accessible",
  "js_dependent",
] as const;

const VisibilityPerformanceCard = ({ site }: Props) => {
  const siteId = site?.id;
  const enabled = !!siteId;
  const [healthyExpanded, setHealthyExpanded] = useState(false);

  const { data: visibilityReports } = useQuery({
    queryKey: ["site_visibility_reports", siteId],
    enabled,
    staleTime: QUERY_STALE_TIME_MS,
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.sites.visibilityReportsDetail(siteId!),
      );
      if (result.error) throw result.error;

      return result.data.data.site_visibility_reports;
    },
  });

  const { data: performanceReports } = useQuery({
    queryKey: ["site_performance_reports", siteId],
    enabled,
    staleTime: QUERY_STALE_TIME_MS,
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.sites.performanceReportsDetail(siteId!),
      );
      if (result.error) throw result.error;

      return result.data.data.site_performance_reports;
    },
  });

  const vis = pickDesktop(visibilityReports);
  const perf = pickDesktop(performanceReports);

  // Hide the card entirely when neither report is available — matches the
  // spec's "Analysis pending" behavior on a fresh site.
  if (!vis && !perf) return null;

  const updatedAt = freshestUpdatedAt(vis, perf);

  const freshnessPill = updatedAt ? (
    <span className={styles.freshness}>
      Updated {formatDistanceToNow(new Date(updatedAt), { addSuffix: true })}
    </span>
  ) : undefined;

  if (isEngineHealthy(site)) {
    return (
      <Card title="Visibility & Performance" button={freshnessPill}>
        <button
          type="button"
          className={styles.healthyHeader}
          onClick={() => setHealthyExpanded((v) => !v)}
          aria-expanded={healthyExpanded}
        >
          <span className={styles.healthyIcon}>
            <Icon iconName="SiteSpeedIcon" width="3rem" height="3rem" />
          </span>
          <div className={styles.healthyText}>
            <div className={styles.healthyTitle}>
              AI Visibility Engine is active
            </div>
            <div className={styles.healthyBody}>
              Bots and LLMs are served fully prerendered content at the edge —
              visibility and performance are optimized automatically.
            </div>
          </div>
          <span
            className={clsx(
              styles.healthyChevron,
              healthyExpanded && styles.healthyChevronOpen,
            )}
            aria-hidden
          >
            <Icon iconName="CaretRight" width="1rem" height="1rem" />
          </span>
        </button>
        {healthyExpanded && (
          <div className={styles.healthyDetails}>
            <div className={styles.sections}>
              {vis ? (
                <VisibilitySection report={vis} />
              ) : (
                <PendingSection label="Search Visibility" />
              )}
              {perf ? (
                <PerformanceSection report={perf} />
              ) : (
                <PendingSection label="Page Performance" />
              )}
            </div>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card title="Visibility & Performance" button={freshnessPill}>
      <div className={styles.sections}>
        {vis ? (
          <VisibilitySection report={vis} />
        ) : (
          <PendingSection label="Search Visibility" />
        )}
        {perf ? (
          <PerformanceSection report={perf} />
        ) : (
          <PendingSection label="Page Performance" />
        )}
      </div>

      {siteId && (
        <Link
          className={styles.cta}
          to={routes.site.visibilityEngineSettings({ id: String(siteId) })}
        >
          <div className={styles.ctaText}>
            <span className={styles.ctaTitle}>
              Activate the AI Visibility Engine
            </span>
            <span className={styles.ctaSubtext}>
              Serve prerendered content to bots and LLMs and lift every page
              into the green band.
            </span>
          </div>
          <span className={styles.ctaArrow}>
            <Icon iconName="ArrowRightIcon" width="1.25rem" height="1.25rem" />
          </span>
        </Link>
      )}
    </Card>
  );
};

const PendingSection = ({ label }: { label: string }) => (
  <div className={styles.section}>
    <span className={styles.sectionLabel}>{label}</span>
    <span className={styles.empty}>
      Analysis pending — check back after the next crawl.
    </span>
  </div>
);

const VisibilitySection = ({ report }: { report: SiteVisibilityReport }) => {
  const total = SEGMENTS.reduce(
    (s, seg) => s + (report.segment_counts[seg] ?? 0),
    0,
  );
  const ratio = Math.min(1, report.avg_text_ratio);
  const band = bandFromValue(ratio);
  const pct = Math.round(ratio * 100);

  const pieData = SEGMENTS.filter(
    (seg) => (report.segment_counts[seg] ?? 0) > 0,
  ).map((seg) => ({
    title: SEGMENT_LABEL[seg],
    value: report.segment_counts[seg] ?? 0,
    color: SEGMENT_COLOR[seg],
  }));

  return (
    <div className={styles.section}>
      <span className={styles.sectionLabel}>Search Visibility</span>

      <div className={styles.visBody}>
        {total > 0 && (
          <div className={styles.pieWrap}>
            <PieChart
              data={pieData}
              lineWidth={28}
              startAngle={270}
              animate={false}
            />
          </div>
        )}

        <div className={styles.visStats}>
          <div className={styles.headlineRow}>
            <span className={`${styles.headlineNumber} ${BAND_CLASS[band]}`}>
              {pct}%
            </span>
            <span className={styles.headlineSubtext}>
              avg text visible to bots
            </span>
          </div>

          {total > 0 && (
            <div className={styles.distLegend}>
              {SEGMENTS.map((seg) => {
                const count = report.segment_counts[seg] ?? 0;

                return (
                  <span key={seg} className={styles.distLegendItem}>
                    <span
                      className={`${styles.distLegendDot} ${SEGMENT_DOT_CLASS[seg]}`}
                    />
                    {SEGMENT_LABEL[seg]} {count}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <span className={styles.pagesAnalyzed}>
        {report.pages_analyzed} pages analyzed
      </span>
    </div>
  );
};

const PerformanceSection = ({ report }: { report: SitePerformanceReport }) => {
  const band = perfBandFromMetrics(
    report.avg_absolute_ttfb,
    report.avg_content_load,
  );

  return (
    <div className={styles.section}>
      <span className={styles.sectionLabel}>Page Performance</span>

      <div className={styles.perfRow}>
        <div className={styles.perfStat}>
          <span className={`${styles.perfStatNumber} ${BAND_CLASS[band]}`}>
            {formatMs(report.avg_content_load)}
          </span>
          <span className={styles.perfStatLabel}>avg content load</span>
          <span className={styles.perfStatDetail}>
            median {formatMs(report.mdn_content_load)} · fastest 10%{" "}
            {formatMs(report.btm_content_load)} · slowest{" "}
            {formatMs(report.max_content_load)}
          </span>
        </div>
        <div className={styles.perfStat}>
          <span className={`${styles.perfStatNumber} ${BAND_CLASS[band]}`}>
            {formatMs(report.avg_absolute_ttfb)}
          </span>
          <span className={styles.perfStatLabel}>avg TTFB</span>
          <span className={styles.perfStatDetail}>
            median {formatMs(report.mdn_absolute_ttfb)} · fastest 10%{" "}
            {formatMs(report.btm_absolute_ttfb)} · slowest{" "}
            {formatMs(report.max_absolute_ttfb)}
          </span>
        </div>
      </div>

      <span className={styles.pagesAnalyzed}>
        {report.pages_analyzed} pages analyzed
      </span>
    </div>
  );
};

export default VisibilityPerformanceCard;
